import { FC, useEffect, useRef, useState } from 'react';
import {
    Grid,
    Typography,
    Chip,
    Button,
    Box,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
} from '@material-ui/core';
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import { RootState } from '../../store';
import { addSelectedBoosterMove, removeSelectedBoosterMove, clearSelectedBoosterMoves } from '../../slices/factorSlice';
import { moveGetAll } from '../../api/move';
import { InputField } from '../input-field';
import { openMoveDialog } from '../../slices/moveSlice';

interface FactorBoosterMovesPanelProps {
    height: number;
    onHide?: () => void;
}

export const FactorBoosterMovesPanel: FC<FactorBoosterMovesPanelProps> = ({ height, onHide }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { selectedBoosterMoves } = useSelector((state: RootState) => state.factor);
    const { moveDialogOpen } = useSelector((state: RootState) => state.move);

    const [moves, setMoves] = useState([]);
    const [total, setTotal] = useState(0);
    const [searchedString, setSearchedString] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        getMoves();
    }, []);

    useEffect(() => {
        getMoves();
    }, [searchedString]);

    // Refresh the list when move dialog closes after successful creation
    // Refresh after the shared New Action dialog closes (see unknown-moves-panel).
    const wasMoveDialogOpenRef = useRef(false);
    useEffect(() => {
        if (wasMoveDialogOpenRef.current && !moveDialogOpen) {
            getMoves();
        }
        wasMoveDialogOpenRef.current = moveDialogOpen;
    }, [moveDialogOpen]);

    const getMoves = async () => {
        setLoading(true);
        try {
            const res = await moveGetAll('name', 'asc', searchedString, 'all', 1, 1000);
            if (res.items) {
                setMoves(res.items);
                setTotal(res.total);
            } else {
                setMoves([]);
                setTotal(0);
            }
        } catch (error) {
            console.error('Error fetching actions:', error);
            setMoves([]);
            setTotal(0);
        }
        setLoading(false);
    };

    const handleMoveToggle = (move: any) => {
        const isSelected = selectedBoosterMoves.some(selectedMove => selectedMove.id === move.id);
        if (isSelected) {
            dispatch(removeSelectedBoosterMove(move.id));
        } else {
            dispatch(addSelectedBoosterMove(move));
        }
    };

    const clearSearch = () => {
        setSearchedString('');
    };

    // Remove client-side filtering since backend now handles multi-field search
    const displayMoves = moves;

    return (
        <Grid item xs={4} sx={{
            backgroundColor: "rgba(255, 123, 0, 0.25)",
            overflowY: "scroll"
        }} padding={0} spacing={2} height={height}>
            <Grid container spacing={2} padding={4} sx={{ paddingLeft: 2 }}>
                <Grid item xs={12} sx={{ paddingLeft: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="h5" style={{ color: "rgb(255, 123, 0)" }}>
                            {t('Select Booster Actions')}
                            <Chip
                                color='default'
                                label={`${selectedBoosterMoves.length}/${total}`}
                                size='small'
                                variant='filled'
                                sx={{ ml: 1 }}
                            />
                        </Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => onHide?.()}
                            sx={{ minWidth: 'auto', px: 2, py: 0.5, fontSize: '0.75rem' }}
                        >
                            {t('Hide')}
                        </Button>
                    </Box>
                </Grid>

                <Grid item xs={12} sx={{ paddingLeft: 0 }}>
                    <Button
                        variant='contained'
                        size='small'
                        fullWidth
                        onClick={() => {
                            dispatch(openMoveDialog({ element: { id: null }, isEditabled: true, isDuplicated: false }));
                        }}
                        style={{ backgroundColor: 'rgb(255, 123, 0)' }}
                    >
                        {t('New action')}
                    </Button>
                </Grid>

                {selectedBoosterMoves.length > 0 && (
                    <Grid item xs={12} sx={{ paddingLeft: 0 }}>
                        <Box sx={{
                            backgroundColor: 'rgba(255, 123, 0, 0.1)',
                            borderRadius: 1,
                            p: 1,
                            mb: 1
                        }}>
                            <Typography variant="body2" color="warning.main" sx={{ fontWeight: 'bold', mb: 1 }}>
                                {t('Selected Booster Actions')} ({selectedBoosterMoves.length})
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selectedBoosterMoves.map((move) => (
                                    <Chip
                                        key={move.id}
                                        label={move.name}
                                        size="small"
                                        onDelete={() => dispatch(removeSelectedBoosterMove(move.id))}
                                        color="warning"
                                        variant="outlined"
                                    />
                                ))}
                            </Box>
                            {selectedBoosterMoves.length > 0 && (
                                <Button
                                    size="small"
                                    color="error"
                                    onClick={() => dispatch(clearSelectedBoosterMoves())}
                                    sx={{ mt: 1 }}
                                >
                                    {t('Clear All')}
                                </Button>
                            )}
                        </Box>
                    </Grid>
                )}

                <Grid item xs={12} sx={{ paddingLeft: 0 }}>
                    <InputField
                        fullWidth
                        placeholder={t('Search actions...')}
                        name="search"
                        onChange={(e) => setSearchedString(e.target.value)}
                        type="text"
                        value={searchedString}
                    />
                </Grid>

                <Grid item xs={12}>
                    <List>
                        {displayMoves.map((move) => {
                            const isSelected = selectedBoosterMoves.some(selectedMove => selectedMove.id === move.id);
                            return (
                                <ListItem disablePadding key={move.id} sx={{ marginBottom: 1 }}>
                                    <ListItemButton
                                        onClick={() => handleMoveToggle(move)}
                                        selected={isSelected}
                                        sx={{
                                            borderRadius: 1,
                                            backgroundColor: isSelected ? 'rgba(255, 123, 0, 0.1)' : 'background.default',
                                            '&.Mui-selected': {
                                                backgroundColor: 'rgba(255, 123, 0, 0.2)',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(255, 123, 0, 0.3)',
                                                },
                                            },
                                        }}
                                    >
                                        <ListItemText
                                            primary={move.name}
                                            secondary={move.description}
                                            primaryTypographyProps={{
                                                variant: 'body2',
                                                fontWeight: isSelected ? 'bold' : 'normal',
                                            }}
                                            secondaryTypographyProps={{
                                                variant: 'caption',
                                                color: 'text.secondary',
                                            }}
                                        />
                                    </ListItemButton>
                                </ListItem>
                            );
                        })}
                    </List>
                </Grid>
            </Grid>
        </Grid>
    );
};